export const cssQuestions = [
  {
    id: 1,
    question: "What are CSS layouts?",
    parts: [
      {
        type: "text",
        content: `**CSS Layouts** define how elements are positioned and sized on a page.

**Main Systems**
- 🧩 Flexbox: one-dimensional (row OR column); content-based sizing; great for components
- 🗺️ Grid: two-dimensional (rows AND columns); track-based sizing; perfect for page layouts
- 📍 Positioning: static, relative, absolute, fixed, sticky

**Best Practices**
- Use Grid for overall page layout
- Use Flexbox inside components`
      },
      {
        type: "code",
        language: "css",
        content: ".container {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  gap: 1rem;\n}\n\n/* Grid example */\n.container {\n  display: grid;\n  grid-template-columns: repeat(3, 1fr);\n  gap: 1rem;\n}"
      }
    ]
  },
  {
    id: 2,
    question: "How can we override CSS styles?",
    parts: [
      {
        type: "text",
        content: `**Overriding CSS** depends on selector specificity and source order.

**Specificity (low → high)**
- 🏷️ Element: \`div\`
- 🎯 Class: \`.button\`
- 🆔 ID: \`#header\`
- ✍️ Inline styles
- ❗ \`!important\` (last resort)

Examples: \`div\` = 1, \`.button\` = 10, \`div.button\` = 11, \`#header .button\` = 110.

**Best Practices**
- Avoid \`!important\` when possible
- Prefer specific class names over IDs
- Keep specificity low for reusability
- Consider CSS Modules or CSS-in-JS`
      },
      {
        type: "code",
        language: "css",
        content: ".my-style {\n  color: red !important;\n}\n\n/* Increasing specificity */\n.container .button { color: blue; }\n.button.primary { color: blue; }"
      }
    ]
  },
  {
    id: 3,
    question: "How is CSS bundled?",
    parts: [
      {
        type: "text",
        content: `Modern tools bundle CSS and support modules.

**Common Tools**
- 🧱 Webpack: \`css-loader\`, \`style-loader\`, \`mini-css-extract-plugin\`
- ⚡ Vite: built-in CSS, PostCSS, CSS Modules

**Process**
1. Import CSS in JS
2. Bundler resolves imports and optimizes
3. Output as injected styles (dev) or a separate file (prod)`
      },
      {
        type: "code",
        language: "javascript",
        content: "import './App.css';\nimport styles from './Button.module.css';\n\n// Dev: injected via <style> tags (HMR)\n// Prod: extracted, minified, cache-busted CSS file"
      }
    ]
  },
  {
    id: 4,
    question: "Explain the CSS Box Model",
    parts: [
      {
        type: "text",
        content: `**CSS Box Model** describes how elements are rendered as rectangular boxes.

**Components (inside → out)**
- 📦 **Content**: actual content (text, images)
- 🎨 **Padding**: space between content and border (transparent, inherits background)
- 🖼️ **Border**: surrounds padding (can have color, style, width)
- 🌊 **Margin**: space outside border (transparent, creates separation between elements)

**Box Sizing**
- \`content-box\` (default): width/height only apply to content
- \`border-box\`: width/height include padding and border

**Key Concepts**
- Margin collapse: vertical margins between elements can merge
- Use \`border-box\` globally for predictable sizing`
      },
      {
        type: "code",
        language: "css",
        content: "/* Default box model */\n.box {\n  width: 300px;\n  padding: 20px;\n  border: 5px solid black;\n  margin: 10px;\n  /* Total width = 300 + 40 + 10 = 350px */\n}\n\n/* Better approach */\n* {\n  box-sizing: border-box;\n}\n\n.box {\n  width: 300px;\n  padding: 20px;\n  border: 5px solid black;\n  /* Total width = 300px (includes padding & border) */\n}"
      }
    ]
  },
  {
    id: 5,
    question: "How would you implement a responsive CSS Grid layout with fallback support?",
    parts: [
      {
        type: "text",
        content: `**Responsive CSS Grid** with fallbacks for older browsers.

**Modern Grid Implementation**
- Use \`grid-template-columns\` with \`fr\` units and \`minmax()\`
- Media queries for breakpoint adjustments
- \`grid-auto-rows\` for consistent heights

**Fallback Strategies**
- \`@supports (display: grid)\` for feature detection
- Flexbox as primary fallback
- Float-based layouts for legacy support

**Best Practices**
- Relative units over fixed pixels
- Test across browser support matrix`
      },
      {
        type: "code",
        language: "css",
        content: ".grid {\n  display: grid;\n  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));\n  gap: 1rem;\n}\n\n/* Fallback */\n@supports not (display: grid) {\n  .grid {\n    display: flex;\n    flex-wrap: wrap;\n  }\n  .grid-item {\n    flex: 1 1 250px;\n  }\n}"
      }
    ]
  },
  {
    id: 6,
    question: "Explain CSS animations vs transitions and when to use each",
    parts: [
      {
        type: "text",
        content: `**Transitions** vs **Animations** for different use cases.

**Transitions**
- Smooth state changes (hover → active)
- Simple interpolation between two values
- Triggered by CSS property changes

**Animations**
- Keyframe-based with full timeline control
- Complex sequences, loops, direction changes
- Independent of state changes

**Performance**
- Prefer GPU-accelerated properties (\`transform\`, \`opacity\`)
- Avoid layout-triggering properties
- Use \`will-change\` for optimization

**Use Cases**
- Transitions: button interactions, simple reveals
- Animations: loaders, complex UI transitions`
      },
      {
        type: "code",
        language: "css",
        content: "/* Transition example */\n.button {\n  transition: background-color 0.3s ease;\n}\n.button:hover {\n  background-color: blue;\n}\n\n/* Animation example */\n@keyframes spin {\n  to { transform: rotate(360deg); }\n}\n.loader {\n  animation: spin 1s linear infinite;\n}"
      }
    ]
  },
  {
    id: 7,
    question: "How do you optimize CSS for performance in large-scale applications?",
    parts: [
      {
        type: "text",
        content: `**CSS Performance** optimization strategies for large apps.

**Delivery Optimization**
- Inline critical CSS above fold
- Lazy-load non-critical stylesheets
- Minify and compress CSS files

**Rendering Performance**
- Use \`transform\`/\`opacity\` for animations
- Avoid forced synchronous layouts
- Leverage \`contain\` for layout isolation

**Architecture**
- Code-split CSS by route/component
- Use CSS Modules to avoid global scope
- Implement caching with immutable assets

**Tools**
- PostCSS for processing
- CSS-in-JS for dynamic styling
- Audit with DevTools Performance tab`
      },
      {
        type: "code",
        language: "javascript",
        content: "// Critical CSS inlining\nimport { renderToString } from 'react-dom/server';\nconst criticalCSS = extractCritical(html);\n\n// Code splitting\nimport('./component.css');\n\n// CSS Modules\nimport styles from './Button.module.css';\n// styles.button -> unique class name"
      }
    ]
  },
  {
    id: 8,
    question: "Explain CSS Containment and its performance benefits",
    parts: [
      {
        type: "text",
        content: `**CSS Containment** allows developers to tell the browser which parts of the page are independent, enabling optimized rendering.

**Types of Containment**
- 🎯 \`layout\`: Isolates layout calculations within the element
- 🎨 \`paint\`: Element's contents won't paint outside its bounds
- 📏 \`size\`: Element's size is independent of its children
- 🎭 \`style\`: Scoped style changes don't affect outside elements

**Performance Benefits**
- Reduces rendering work by isolating changes
- Enables browser to skip recalculating unaffected areas
- Critical for large DOM trees and infinite scrolling
- Improves FPS in complex animations

**Best Practices**
- Use for independent components (cards, list items)
- Apply to virtualized list items
- Combine with \`content-visibility\` for better performance`
      },
      {
        type: "code",
        language: "css",
        content: "/* Basic containment */\n.card {\n  contain: layout paint;\n  /* Changes inside won't trigger reflows outside */\n}\n\n/* Strict containment */\n.virtualized-list-item {\n  contain: strict; /* layout + paint + size */\n}\n\n/* With content-visibility */\n.lazy-section {\n  content-visibility: auto;\n  contain-intrinsic-size: 0 500px;\n}"
      }
    ]
  },
  {
    id: 9,
    question: "How do CSS Custom Properties differ from preprocessor variables, and what are advanced use cases?",
    parts: [
      {
        type: "text",
        content: `**CSS Custom Properties** are runtime values that inherit and cascade, unlike preprocessor variables which are compile-time only.

**Key Differences**
- 🔄 Runtime vs compile-time
- 🌊 Cascade and inherit
- 🎮 Manipulable with JavaScript
- 🎯 Can be scoped to specific elements

**Advanced Use Cases**
- Dynamic theming with calc()
- Responsive design without media queries
- Context-aware components
- Animation with typed properties

**Best Practices**
- Use fallback values: \`var(--color, blue)\`
- Prefix custom properties: \`--brand-primary\`
- Leverage JavaScript for dynamic updates
- Combine with calc() for computed values`
      },
      {
        type: "code",
        language: "css",
        content: "/* Dynamic theming with calc() */\n:root {\n  --primary-h: 200;\n  --primary-s: 80%;\n  --primary-l: 50%;\n}\n\n.button {\n  background: hsl(var(--primary-h), var(--primary-s), var(--primary-l));\n}\n\n.button:hover {\n  background: hsl(var(--primary-h), var(--primary-s), calc(var(--primary-l) - 10%));\n}\n\n/* Context-aware components */\n.card {\n  --card-bg: white;\n  background: var(--card-bg);\n}\n\n.dark-mode .card {\n  --card-bg: #333;\n}"
      }
    ]
  },
  {
    id: 10,
    question: "What is CSS Houdini and how does it enable custom rendering behaviors?",
    parts: [
      {
        type: "text",
        content: `**CSS Houdini** is a set of low-level browser APIs that expose parts of the CSS rendering engine, allowing custom CSS functionality.

**Key APIs**
- 🎨 Paint API: Custom paint worklets for backgrounds
- 📐 Properties & Values API: Typed custom properties
- 📏 Layout API: Custom layout algorithms
- 🎬 Animation API: Custom animation effects

**Benefits**
- Polyfill CSS features without JS runtime cost
- Runs in compositor thread (performant)
- Type-safe custom properties enable smooth animations
- Better separation of concerns

**Use Cases**
- Custom tooltip positioning
- Animated backgrounds without canvas
- Masonry/Pinterest layouts
- Smooth parallax effects

**Considerations**
- Browser support varies (progressive enhancement needed)
- Requires worklet registration
- Performance depends on implementation`
      },
      {
        type: "code",
        language: "javascript",
        content: "// Paint API - Custom gradient\nregisterPaint('customGradient', class {\n  paint(ctx, size, properties) {\n    const gradient = ctx.createLinearGradient(0, 0, size.width, size.height);\n    gradient.addColorStop(0, '#ff6b6b');\n    gradient.addColorStop(1, '#4ecdc4');\n    ctx.fillStyle = gradient;\n    ctx.fillRect(0, 0, size.width, size.height);\n  }\n});\n\n// CSS usage\n// .element { background: paint(customGradient); }\n\n// Properties & Values API\nCSS.registerProperty({\n  name: '--rotation',\n  syntax: '<angle>',\n  initialValue: '0deg',\n  inherits: false\n});"
      }
    ]
  }
];

